import React, { useEffect, useState, useCallback, useMemo } from 'react';
import type { User } from '@supabase/supabase-js';
import { examService } from '@/services/examService';
import type { ExamEligibility } from '@/types/exam';
import ExamTakingModal from '../ExamTakingModal';
import CertificatePreview from '../CertificatePreview';
import { AlertCircle, Award, CheckCircle2, Lock, Book, Timer, Zap, TrendingUp, Trophy, ArrowRight } from 'lucide-react';
import { supabase } from '@/config/supabase';

interface ExamViewProps {
  user: User | null;
  theme?: 'light' | 'dark';
  isPro?: boolean;
  onUpgradeClick?: () => void;
}

const ExamView: React.FC<ExamViewProps> = ({ user, theme = 'dark', isPro = false, onUpgradeClick }) => {
  const isDark = theme === 'dark';
  const [eligibleExams, setEligibleExams] = useState<ExamEligibility[]>([]);
  const [featuredRoadmaps, setFeaturedRoadmaps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedExam, setSelectedExam] = useState<ExamEligibility | null>(null);
  const [showExamModal, setShowExamModal] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [certificateId, setCertificateId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [visibleCards, setVisibleCards] = useState<Set<string>>(new Set());

  // Memoize theme styles
  const themeStyles = useMemo(() => ({
    bgGradient: isDark ? 'bg-gradient-to-b from-slate-900 to-slate-950' : 'bg-gradient-to-b from-slate-50 to-slate-100',
    textPrimary: isDark ? 'text-white' : 'text-slate-900',
    textSecondary: isDark ? 'text-slate-400' : 'text-slate-600',
  }), [isDark]);

  useEffect(() => {
    loadExams();
  }, [user?.id]);

  useEffect(() => {
    if (eligibleExams.length === 0 && user?.id) {
      loadFeaturedRoadmaps();
    }
  }, [eligibleExams.length, user?.id, user?.email]);

  useEffect(() => {
    if (eligibleExams.length === 0) return;
    const timers: NodeJS.Timeout[] = [];
    eligibleExams.forEach((exam, index) => {
      const timer = setTimeout(() => {
        setVisibleCards((prev) => new Set([...prev, exam.roadmap_id]));
      }, index * 100);
      timers.push(timer);
    });
    return () => timers.forEach(clearTimeout);
  }, [eligibleExams.length]);

  const loadExams = useCallback(async () => {
    if (!user?.id) {
      setError('Please log in to view exams');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const exams = await examService.getEligibleExams(user.id, user.email);
      setEligibleExams(exams);
    } catch (err) {
      console.error('Error loading exams:', err);
      setError(`Error: ${err instanceof Error ? err.message : 'Failed to load exams'}`);
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.email]);

  const loadFeaturedRoadmaps = useCallback(async () => {
    if (!user?.id && !user?.email) return;

    try {
      // Get user's roadmaps (created or viewed/bookmarked)
      let roadmapIds: string[] = [];

      // Query by user_id
      if (user?.id) {
        const { data: roadmapsByUserId, error: errorByUserId } = await supabase
          .from('user_roadmaps')
          .select('roadmap_id')
          .eq('user_id', user.id);
        
        if (!errorByUserId) {
          roadmapIds = [...(roadmapsByUserId?.map((r: any) => r.roadmap_id) || [])];
        }
      }

      // Query by email to capture all user's roadmaps
      if (user?.email) {
        const { data: roadmapsByEmail, error: errorByEmail } = await supabase
          .from('user_roadmaps')
          .select('roadmap_id')
          .eq('email', user.email);
        
        if (!errorByEmail) {
          roadmapIds = [...new Set([...roadmapIds, ...(roadmapsByEmail?.map((r: any) => r.roadmap_id) || [])])];
        }
      }

      if (roadmapIds.length === 0) {
        setFeaturedRoadmaps([]);
        return;
      }

      // Get roadmap details for user's roadmaps
      const { data: roadmaps } = await supabase
        .from('roadmaps')
        .select('id, title, intensity, months')
        .in('id', roadmapIds)
        .limit(6);

      setFeaturedRoadmaps(roadmaps || []);
    } catch (err) {
      console.error('Error loading featured roadmaps:', err);
    }
  }, [user?.id, user?.email]);

  const getRoadmapStats = useCallback((roadmap: any) => {
    const monthsCount = (roadmap.months || []).length || 0;
    const totalDays = (roadmap.months || []).reduce((acc: number, month: any) => {
      return acc + ((month.weeks || []).reduce((weekAcc: number, week: any) => weekAcc + ((week.days || []).length), 0));
    }, 0) || 28;
    return { monthsCount, totalDays };
  }, []);

  const handleStartExam = useCallback((exam: ExamEligibility) => {
    if (!exam.is_eligible) {
      setError(`Complete ${Math.ceil(80 - exam.completion_percentage)}% more to unlock`);
      return;
    }
    setSelectedExam(exam);
    setShowExamModal(true);
  }, []);

  const handleExamCompleted = useCallback((examAttemptId: string) => {
    setShowExamModal(false);
    setCertificateId(examAttemptId);
    setShowCertificate(true);
    loadExams();
  }, [loadExams]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-12 px-4 ${themeStyles.bgGradient}`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className={`text-4xl font-bold mb-3 flex items-center justify-center gap-3 ${themeStyles.textPrimary}`}>
            <Award className="w-10 h-10 text-red-600" />
            Skill Assessment Exams
          </h1>
          <p className={`text-lg ${themeStyles.textSecondary}`}>Validate your skills and earn certificates</p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Teaser Section - For Non-Pro Users */}
        {!isPro && (
          <div className="mb-12">
            <div className={`relative rounded-3xl overflow-hidden p-8 md:p-10 mb-8 border bg-gradient-to-br ${
              isDark ? 'from-red-900/20 via-rose-900/10 to-orange-900/20 border-red-500/30' : 'from-red-50 to-rose-50 border-red-200'
            }`}>
              <div className="absolute inset-0 opacity-30">
                <div className="absolute inset-0 bg-gradient-to-r from-red-500/0 via-orange-500/20 to-rose-500/0 animate-pulse"></div>
              </div>

              <div className="relative">
                <div className="flex items-start gap-4 mb-6">
                  <Trophy className="w-8 h-8 text-red-500 animate-bounce flex-shrink-0" />
                  <div>
                    <h2 className={`text-3xl font-bold mb-2 ${themeStyles.textPrimary}`}>Unlock Your Potential with Certification Exams</h2>
                    <p className={`text-lg mb-4 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      Ready to <span className="font-bold text-red-500">prove your mastery</span> and stand out? NanoLez exams validate your skills with verified certificates employers recognize.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                  {[
                    { icon: Zap, title: 'Instant Validation', desc: 'Get verified credentials instantly', color: 'text-red-500' },
                    { icon: TrendingUp, title: 'Career Boost', desc: 'Impress employers with certificates', color: 'text-orange-500' },
                    { icon: Award, title: 'Lifetime Value', desc: 'Certificates never expire', color: 'text-rose-500' },
                  ].map(({ icon: Icon, title, desc, color }) => (
                    <div key={title} className={`p-4 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white/60 border-white'}`}>
                      <Icon className={`w-6 h-6 ${color} mb-2`} />
                      <h3 className={`font-bold mb-1 ${themeStyles.textPrimary}`}>{title}</h3>
                      <p className={`text-sm ${themeStyles.textSecondary}`}>{desc}</p>
                    </div>
                  ))}
                </div>

                <button
                  onClick={onUpgradeClick}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-rose-600 text-white font-bold rounded-xl hover:shadow-2xl hover:shadow-red-500/50 transition-all transform hover:scale-105"
                >
                  <span>Upgrade to Pro & Start Exams</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Preview */}
            {featuredRoadmaps.length > 0 && (
              <div>
                <h3 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${themeStyles.textPrimary}`}>
                  <Lock className="w-6 h-6 text-red-500" />
                  See What You'll Unlock
                </h3>
                <p className={`mb-6 ${themeStyles.textSecondary}`}>Preview the exams available with Pro subscription</p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {featuredRoadmaps.map((roadmap) => {
                    const { monthsCount, totalDays } = getRoadmapStats(roadmap);
                    return (
                      <div
                        key={roadmap.id}
                        className={`group relative min-h-[280px] rounded-3xl p-6 overflow-hidden backdrop-blur-sm transition-all border ${
                          isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white/50 border-white/60'
                        }`}
                      >
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20 pointer-events-none rounded-3xl" />
                        <div className="absolute top-4 right-4 z-10 p-2.5 rounded-full ${isDark ? 'bg-red-500/20' : 'bg-red-100'}">
                          <Lock className="w-5 h-5 text-red-500" />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/30 group-hover:to-black/40 transition-all rounded-3xl" />

                        <div className="relative h-full flex flex-col">
                          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 via-orange-500 to-rose-500" />
                          <div className="mb-4">
                            <span className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'}`}>
                              {roadmap.intensity}
                            </span>
                            <h3 className={`text-lg font-bold leading-snug line-clamp-2 mt-2 ${themeStyles.textPrimary}`}>{roadmap.title}</h3>
                          </div>
                          <div className={`flex items-center gap-4 text-sm mb-auto ${themeStyles.textSecondary}`}>
                            <div className="flex items-center gap-1.5">
                              <Book className="w-4 h-4" />
                              <span>{monthsCount} months</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Zap className="w-4 h-4" />
                              <span>{totalDays} days</span>
                            </div>
                          </div>
                          <div className="mt-6 pt-4 border-t border-white/10">
                            <button
                              onClick={onUpgradeClick}
                              className={`w-full py-2 px-4 rounded-lg font-semibold transition-all text-sm flex items-center justify-center gap-2 ${isDark ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-red-100 text-red-600 hover:bg-red-200'}`}
                            >
                              <span>Unlock</span>
                              <ArrowRight className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Exams Grid - Only for Pro Users */}
        {eligibleExams.length > 0 && isPro && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {eligibleExams.map((exam) => {
              const { monthsCount, totalDays } = getRoadmapStats(exam);
              const completedCount = Number(exam.completed_days) || 0;
              const completedPercentage = totalDays > 0 ? Math.round((completedCount / totalDays) * 100) : 0;
              const studyTime = exam.meta?.studyTime || 2;
              const isVisible = visibleCards.has(exam.roadmap_id);

              return (
                <div
                  key={exam.roadmap_id}
                  onClick={() => handleStartExam(exam)}
                  className={`group relative min-h-[240px] md:min-h-[280px] rounded-3xl p-6 cursor-pointer transition-all card-lift overflow-hidden border ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  } duration-500 ease-out ${isDark ? 'bg-white/[0.03] border-white/5 hover:bg-white/[0.06]' : 'bg-white border-zinc-200/80 hover:border-zinc-300 shadow-sm hover:shadow-xl'}`}
                >
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 via-orange-500 to-rose-500" />

                  <div className="h-full flex flex-col">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'}`}>
                            {exam.intensity}
                          </span>
                        </div>
                        <h3 className={`text-lg font-bold leading-snug line-clamp-2 ${themeStyles.textPrimary}`}>{exam.title || exam.roadmap_name}</h3>
                      </div>
                    </div>

                    <div className={`flex items-center gap-4 text-sm mb-auto ${themeStyles.textSecondary}`}>
                      <div className="flex items-center gap-1.5">
                        <Book className="w-4 h-4" />
                        <span>{monthsCount} months</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Timer className="w-4 h-4" />
                        <span>{studyTime}h/day</span>
                      </div>
                    </div>

                    <div className="mt-6 space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className={themeStyles.textSecondary}>Progress</span>
                        <span className="font-semibold text-red-500">{completedPercentage}%</span>
                      </div>
                      <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-zinc-100'}`}>
                        <div
                          className="h-full bg-gradient-to-r from-red-500 to-rose-500 rounded-full transition-all duration-700"
                          style={{ width: `${completedPercentage}%` }}
                        />
                      </div>
                      <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                        {completedCount} of {totalDays} days completed
                      </p>
                    </div>

                    <div className="mt-4">
                      {exam.is_eligible ? (
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold ${isDark ? 'bg-green-500/30 text-green-300 animate-pulse' : 'bg-green-100 text-green-700 animate-pulse'}`}>
                          <CheckCircle2 className="w-5 h-5" />
                          🚀 Prove Your Mastery
                        </div>
                      ) : (
                        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                          <Lock className="w-4 h-4" />
                          {Math.ceil(totalDays * 0.8 - completedCount)} days more
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showExamModal && selectedExam && (
        <ExamTakingModal
          exam={selectedExam}
          user={user}
          onClose={() => setShowExamModal(false)}
          onCompleted={handleExamCompleted}
        />
      )}

      {showCertificate && certificateId && (
        <CertificatePreview
          examAttemptId={certificateId}
          onClose={() => {
            setShowCertificate(false);
            setCertificateId(null);
          }}
        />
      )}
    </div>
  );
};

export default ExamView;

